export const STANDARD_IMAGE_TYPES = {
  RGB: 'rgb',
  EVI: 'evi',
  NDVI: 'ndvi',
  ALTIMETRY: 'altimetry',
  SOIL_MAP: 'soil_map',
  YIELD_MAP: 'yield_map',
};

export const HD_IMAGE_TYPES = {
  RGB: 'rgb_hd',
  EVI: 'evi_hd',
  NDVI: 'ndvi_hd',
  NDRE: 'ndre_hd',
};

export const IMAGE_TYPES_WITH_DATE = new Set([
  STANDARD_IMAGE_TYPES.RGB,
  STANDARD_IMAGE_TYPES.EVI,
  STANDARD_IMAGE_TYPES.NDVI,
  HD_IMAGE_TYPES.RGB,
  HD_IMAGE_TYPES.EVI,
  HD_IMAGE_TYPES.NDVI,
  HD_IMAGE_TYPES.NDRE,
]);

export const IMAGE_AMBIENT_SOURCE_TYPES = new Set([
  STANDARD_IMAGE_TYPES.EVI,
  HD_IMAGE_TYPES.EVI,
]);

const BASE_IMAGE_TYPE_BY_TYPE = {
  [STANDARD_IMAGE_TYPES.RGB]: STANDARD_IMAGE_TYPES.RGB,
  [STANDARD_IMAGE_TYPES.EVI]: STANDARD_IMAGE_TYPES.EVI,
  [STANDARD_IMAGE_TYPES.NDVI]: STANDARD_IMAGE_TYPES.NDVI,
  [STANDARD_IMAGE_TYPES.SOIL_MAP]: STANDARD_IMAGE_TYPES.SOIL_MAP,
  [STANDARD_IMAGE_TYPES.YIELD_MAP]: STANDARD_IMAGE_TYPES.YIELD_MAP,
  [HD_IMAGE_TYPES.RGB]: STANDARD_IMAGE_TYPES.RGB,
  [HD_IMAGE_TYPES.EVI]: STANDARD_IMAGE_TYPES.EVI,
  [HD_IMAGE_TYPES.NDVI]: STANDARD_IMAGE_TYPES.NDVI,
  [HD_IMAGE_TYPES.NDRE]: 'ndre',
};

const API_LAYER_BY_TYPE = {
  [STANDARD_IMAGE_TYPES.RGB]: 'visible',
  [STANDARD_IMAGE_TYPES.EVI]: 'evi',
  [STANDARD_IMAGE_TYPES.NDVI]: 'ndvi',
  [HD_IMAGE_TYPES.RGB]: 'visible',
  [HD_IMAGE_TYPES.EVI]: 'evi',
  [HD_IMAGE_TYPES.NDVI]: 'ndvi',
  [HD_IMAGE_TYPES.NDRE]: 'ndre',
};

const MAP_LAYER_TYPE_BY_TYPE = {
  [STANDARD_IMAGE_TYPES.RGB]: 'visible',
  [STANDARD_IMAGE_TYPES.EVI]: 'evi',
  [STANDARD_IMAGE_TYPES.NDVI]: 'ndvi',
  [STANDARD_IMAGE_TYPES.ALTIMETRY]: 'altimetry',
  [STANDARD_IMAGE_TYPES.SOIL_MAP]: 'soil_map',
  [STANDARD_IMAGE_TYPES.YIELD_MAP]: 'yield_map',
  [HD_IMAGE_TYPES.RGB]: 'visible_hd',
  [HD_IMAGE_TYPES.EVI]: 'evi_hd',
  [HD_IMAGE_TYPES.NDVI]: 'ndvi_hd',
  [HD_IMAGE_TYPES.NDRE]: 'ndre_hd',
};

const TRANSLATION_KEY_BY_TYPE = {
  [STANDARD_IMAGE_TYPES.RGB]: 'visible',
  [STANDARD_IMAGE_TYPES.EVI]: 'evi',
  [STANDARD_IMAGE_TYPES.NDVI]: 'ndvi',
  [STANDARD_IMAGE_TYPES.ALTIMETRY]: 'altimetry',
  [STANDARD_IMAGE_TYPES.SOIL_MAP]: 'soil_map',
  [STANDARD_IMAGE_TYPES.YIELD_MAP]: 'yield_map',
  [HD_IMAGE_TYPES.RGB]: 'visible_hd',
  [HD_IMAGE_TYPES.EVI]: 'evi_hd',
  [HD_IMAGE_TYPES.NDVI]: 'ndvi_hd',
  [HD_IMAGE_TYPES.NDRE]: 'ndre_hd',
};

export const isHdImageType = (imageType) => Object.values(HD_IMAGE_TYPES).includes(imageType);

export const isImageAmbientSourceType = (imageType) => IMAGE_AMBIENT_SOURCE_TYPES.has(imageType);

export const getBaseImageType = (imageType) => BASE_IMAGE_TYPE_BY_TYPE[imageType] || imageType;

export const getApiLayerForImageType = (imageType) => API_LAYER_BY_TYPE[imageType] || imageType;

export const getMapLayerImageType = (imageType) => MAP_LAYER_TYPE_BY_TYPE[imageType] || imageType;

export const getImageQuality = (imageType) => (isHdImageType(imageType) ? 'hd' : 'standard');

export const getRemoteSensingParams = (imageType, params = {}) => {
  if (!isHdImageType(imageType)) {
    return params;
  }

  return {
    ...params,
    quality: 'hd',
  };
};

export const getImageTypeTranslationKey = (imageType) => (
  TRANSLATION_KEY_BY_TYPE[imageType] || 'no_index'
);

export const isHdAccessActive = (access) => {
  if (!access) {
    return false;
  }

  const assignedHa = Number(
    access.assigned_hd_ha
      ?? access.assigned_ha
      ?? access.total_hd_ha
      ?? access.total_ha
      ?? 0
  );
  const hasAssignedHa = Number.isFinite(assignedHa) && assignedHa > 0;
  const backendActive = access.active ?? access.is_active ?? access.enabled ?? true;

  if (!hasAssignedHa || backendActive === false) {
    return false;
  }

  if (!access.period_end && !access.hd_period_end) {
    return true;
  }

  const periodEnd = new Date(access.period_end || access.hd_period_end);

  if (Number.isNaN(periodEnd.getTime())) {
    return true;
  }

  return periodEnd >= new Date();
};

export const getHdRequiredHa = (access) => {
  const required = Number(
    access?.required_ha
      ?? access?.required_hd_ha
      ?? access?.new_area_ha
      ?? access?.delta_ha
      ?? 0
  );

  return Number.isFinite(required) ? Math.max(required, 0) : 0;
};

export const getHdAuthorizationId = (access) => (
  access?.authorization_id
    ?? access?.hd_authorization_id
    ?? access?.access_id
    ?? null
);

export const getHdAvailableHa = (access) => {
  const explicitAvailable = Number(
    access?.available_hd_ha
      ?? access?.available_ha
      ?? access?.remaining_hd_ha
      ?? access?.remaining_ha
  );

  if (Number.isFinite(explicitAvailable)) {
    return explicitAvailable;
  }

  const assigned = Number(access?.assigned_hd_ha ?? access?.assigned_ha ?? 0);
  const used = Number(access?.used_hd_ha ?? access?.used_ha ?? access?.covered_ha ?? 0);

  if (!Number.isFinite(assigned)) {
    return 0;
  }

  return assigned - (Number.isFinite(used) ? used : 0);
};
